import { ApprovalCard, type ApprovalSection } from "~/components/tool-ui/approval-card";
import { useFetcher } from "~/hooks/useFetcher";
import type { AgentCapability } from "~/services/agents";
import { projectAgentsPath } from "~/services/projects";

interface HandoffSuggestionProps {
  // Project whose agent roster carries the suggested peer's capability. The
  // project layout and the composer's agent switcher already load that roster, so
  // the card's read is a cache hit, not a request of its own.
  projectId?: string;
  // Id of the suggested peer. Its capability — description and granted toolkits —
  // is read from the roster to fill the card, so the user sees what they'd
  // switch to.
  agentId: string;
  // Display name of the suggested peer, resolved by the parent from the roster.
  agentName: string;
  // Accepting switches to the suggested agent and re-poses the message that
  // produced the suggestion to it — the parent owns both the switch and the
  // send. There is no separate server write; the switch rides the re-posed send.
  onAccept: () => void;
  // Dismissing hides this suggestion. The parent records the dismissed
  // suggesting-turn id in sessionStorage, so it stays hidden across a reload
  // while a later turn's new suggestion still shows.
  onDismiss: () => void;
}

// A boundary affordance offering the latest turn's machine-recorded handoff
// recommendation as a capability preview: the suggested peer's description and
// granted tools, with actions to switch to it for the next message or dismiss
// it. Non-blocking — it never pauses the run; accepting is a plain next-turn
// switch, not a human-in-the-loop approval.
export const HandoffSuggestion = ({
  projectId,
  agentId,
  agentName,
  onAccept,
  onDismiss,
}: HandoffSuggestionProps) => {
  const { data: roster } = useFetcher<AgentCapability[]>(
    projectId ? projectAgentsPath(projectId) : null
  );
  const capability = roster?.find((rosterAgent) => rosterAgent.id === agentId);

  const sections: ApprovalSection[] | undefined =
    capability && capability.toolkits.length > 0
      ? [
          {
            heading: "Tools",
            items: capability.toolkits.map((toolkit) => ({
              label: toolkit.display_name,
              description: toolkit.description,
            })),
          },
        ]
      : undefined;

  return (
    // `pl-10` clears the assistant turn's avatar gutter (`w-7` + `gap-3`) so the
    // card's left edge lines up with the message text above it rather than the
    // transcript's far-left edge.
    <li className="py-2 pl-10" aria-label="Handoff suggestion">
      <ApprovalCard
        id={`handoff-suggestion-${agentId}`}
        title={`Hand off to ${agentName}?`}
        description={capability?.description || undefined}
        sections={sections}
        collapsibleSections
        variant="default"
        confirmLabel={`Switch to ${agentName}`}
        cancelLabel="Dismiss"
        onConfirm={onAccept}
        onCancel={onDismiss}
      />
    </li>
  );
};
